import { getAllEquipment } from 'utils/EquipmentWebAPIUtils';
import { buildQuery } from 'components/design-system/utils/buildQuery';
import { makeEquipmentFilters, buildEquipmentDatesPayload, DATE_RANGE_FIELD_IDS, } from './Equipment.filters';
const PER_PAGE = 50;
// `populate` spec — identical to the legacy List.fetchEquipment call.
const POPULATE = [
    { path: 'owner', select: 'company_name _type' },
    { path: 'shipment', select: 'shipment_id' },
    { path: 'current_shipment', select: 'shipment_id' },
    { path: 'order', select: 'order_id order_type status' },
    {
        path: 'chassis_owner',
        select: 'name company_name _type first_name last_name',
    },
];
const FILTERS = makeEquipmentFilters();
/** TanStack sort → legacy `sort_by` + `order`. Defaults to newest-first by _id. */
function toSort(sorting) {
    if (!(sorting === null || sorting === void 0 ? void 0 : sorting.length))
        return { sort_by: '_id', order: 'desc' };
    const { id, desc } = sorting[0];
    return { sort_by: id, order: desc ? 'desc' : 'asc' };
}
/**
 * Build the equipment query payload — the exact shape v1 sent to
 * `EquipmentActionCreators.getAllEquipment`:
 *
 *   - flat filter params (`equipment`, `status`) via the shared buildQuery
 *   - pagination/sort (`paginate`, `page`, `per_page`, `sort_by`, `order`)
 *   - the nested `dates` payload (the four date-range filters fold into
 *     `dates[field] = { start_date?, end_date? }`, with v1's single-bound rule:
 *     a lone end date is sent as `start_date`).
 *
 * The shared `buildQuery#isEmpty` omits single-bound date ranges, so any date
 * keys it emits are stripped and the `dates` payload is re-derived directly
 * from the raw values via `buildEquipmentDatesPayload` (mirrors v1's
 * `getFiltersWithoutDates` + `buildFilters` split). Exported for parity tests.
 */
export function buildEquipmentQuery(values, page, pageSize, sort) {
    const q = buildQuery(FILTERS, values, Object.assign({ paginate: true, page, per_page: pageSize }, toSort(sort)));
    for (const field of DATE_RANGE_FIELD_IDS)
        delete q[field];
    return Object.assign(Object.assign({}, q), buildEquipmentDatesPayload(values));
}
/**
 * Typed async equipment fetcher for `useListPage` (server pagination + sort).
 * Awaits the underlying promise API `getAllEquipment` (the same util the
 * legacy `EquipmentWebAPIUtils.getAllEquipment` dispatch action calls) and maps
 * its `entity` ({ items, pages, total_entries }) into a PagedFetchResult.
 */
export async function fetchEquipment({ values, page, pageSize, sort, }) {
    const query = Object.assign({ populate: POPULATE }, buildEquipmentQuery(values, page, pageSize, sort));
    const res = (await getAllEquipment(query));
    const entity = (res === null || res === void 0 ? void 0 : res.entity) || {};
    const items = entity.items || [];
    // The endpoint omits `total_entries` on some responses; fall back to the
    // loaded row count so the footer doesn't read "No results" with rows showing
    // (mirrors CustomerDetailsStore: `total_entries || quotes.length`).
    return {
        items,
        totalCount: entity.total_entries || items.length,
        totalPages: entity.pages || 1,
    };
}
export { PER_PAGE };
